import { api } from "@page/api/client";
import { extractErrorMessage } from "@page/api/utils";
import { useBackButton } from "@page/hooks/use-back-button";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { retrieveLaunchParams } from "@telegram-apps/sdk-react";
import { useEffect, useState } from "react";

/**
 * 入口路由（`/telegram-app/`，对应 BotFather `/newapp` 注册的 Web App URL）。
 * 群聊 deep link `t.me/<bot>/<short>?startapp=<prefix>_<chatId>_<tgMsgId>`
 * 唯一会落到这里。读 `start_param` 前缀决定跳哪去：
 *   r_<chatId>_<tgMsgId>  → /telegram-app/reminders?accountId=&emailMessageId=&token=
 *   m_<chatId>_<tgMsgId>  → /telegram-app/mail/$id?accountId=&t=
 *   p_<page>              → 群聊菜单入口，如 p_accounts / p_users / p_list_unread
 *   无 start_param         → /telegram-app/reminders（列表模式，主菜单"我的提醒"）
 */
const RouterPage = () => {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  // 路由中转页本身不是停留页，不显示 BackButton
  useBackButton(undefined);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      let startParam = "";
      try {
        startParam = retrieveLaunchParams().tgWebAppData?.start_param ?? "";
      } catch {
        // 非 TG 环境（直接打开 /telegram-app/）→ 当作无 start_param 走默认路径
      }

      if (!startParam) {
        navigate({ to: "/telegram-app/reminders", search: {}, replace: true });
        return;
      }

      if (navigateMenuStartParam(startParam, navigate)) return;

      const m = startParam.match(/^([rm])_(-?\d+)_(\d+)$/);
      if (!m) {
        setError("无效的入口参数");
        return;
      }
      const feature = m[1];
      const chatId = m[2];
      const tgMsgId = m[3];

      try {
        const { data, error } = await api.api.reminders["resolve-context"].get({
          query: { start: `${chatId}_${tgMsgId}` },
        });
        if (cancelled) return;
        if (error) throw error;
        const ctx = data;
        if (feature === "m") {
          navigate({
            to: "/telegram-app/mail/$id",
            params: { id: ctx.emailMessageId },
            search: { accountId: ctx.accountId, t: ctx.token },
            replace: true,
          });
        } else {
          navigate({
            to: "/telegram-app/reminders",
            search: {
              accountId: ctx.accountId,
              emailMessageId: ctx.emailMessageId,
              token: ctx.token,
            },
            replace: true,
          });
        }
      } catch (e) {
        if (cancelled) return;
        setError(await extractErrorMessage(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="max-w-sm w-full rounded-xl border border-red-900/50 bg-red-950/30 p-6 text-center">
          <p className="text-sm text-red-400">{error}</p>
        </div>
      </div>
    );
  }
  return <div className="min-h-screen bg-black" aria-hidden="true" />;
};

const navigateMenuStartParam = (
  startParam: string,
  navigate: ReturnType<typeof useNavigate>,
): boolean => {
  switch (startParam) {
    case "p_accounts":
      navigate({ to: "/telegram-app/accounts", replace: true });
      return true;
    case "p_users":
      navigate({ to: "/telegram-app/users", replace: true });
      return true;
    case "p_reminders":
      navigate({ to: "/telegram-app/reminders", search: {}, replace: true });
      return true;
    case "p_search":
      navigate({ to: "/telegram-app/search", search: {}, replace: true });
      return true;
    case "p_list_unread":
      navigate({
        to: "/telegram-app/list/$type",
        params: { type: "unread" },
        replace: true,
      });
      return true;
    case "p_list_starred":
      navigate({
        to: "/telegram-app/list/$type",
        params: { type: "starred" },
        replace: true,
      });
      return true;
    case "p_list_junk":
      navigate({
        to: "/telegram-app/list/$type",
        params: { type: "junk" },
        replace: true,
      });
      return true;
    case "p_list_archived":
      navigate({
        to: "/telegram-app/list/$type",
        params: { type: "archived" },
        replace: true,
      });
      return true;
    default:
      return false;
  }
};

export const Route = createFileRoute("/telegram-app/")({
  component: RouterPage,
});
